// IGK-2P B-block — Swap Picker. Click any cast/costume/corner chip → pick a
// replacement sourced ONLY from THIS project's Asset Bible. Scene-scope by
// default with a LOUD shot-only override. Shows blast radius ("stales N
// sketches, M keyframes") BEFORE commit. Portalled full-screen overlay.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from '../../utils/toast';
import { getBibleCards, getSwapBlastRadius, swapBinding } from './igk2pApi';

export default function SwapPicker({ shot, kind = 'cast', role = '', onClose, onReload }) {
  const pid = shot?.project_id;
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('scene');   // scene (default) | shot
  const [picked, setPicked] = useState(null);
  const [blast, setBlast] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getBibleCards(pid).then((r) => setCards(r[kind] || [])).catch(() => setCards([])).finally(() => setLoading(false));
  }, [pid, kind]);

  useEffect(() => {
    getSwapBlastRadius(pid, { scene_number: shot?.scene_number, role, scope })
      .then(setBlast).catch(() => setBlast(null));
  }, [pid, role, scope, shot?.scene_number]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const apply = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const r = await swapBinding(shot.id, { kind, role, card_id: picked.id, scope });
      toast.success(`Swapped to ${r.card} · ${r.updated} shot(s) restaged`);
      onReload?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Swap failed'); }
    finally { setBusy(false); }
  };

  return createPortal((
    <div className="fixed inset-0 z-[210] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`swap-picker-${shot.id}`} onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0c0c10] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-white">Swap {kind}{role ? ` · ${role}` : ''}</div>
            <div className="text-[10px] text-white/40">Pick from this project's Asset Bible only</div>
          </div>
          <button onClick={onClose} data-testid="swap-picker-close" className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Scope toggle — scene default, loud shot override */}
        <div className="flex items-center gap-2 mb-3">
          <button data-testid="swap-scope-scene" onClick={() => setScope('scene')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium border ${scope === 'scene' ? 'bg-fuchsia-500/25 text-fuchsia-100 border-fuchsia-400/40' : 'bg-white/5 text-white/50 border-white/15'}`}>Whole scene (default)</button>
          <button data-testid="swap-scope-shot" onClick={() => setScope('shot')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium border ${scope === 'shot' ? 'bg-amber-500/25 text-amber-100 border-amber-400/50' : 'bg-white/5 text-white/50 border-white/15'}`}>This shot only</button>
          {scope === 'shot' && <span className="inline-flex items-center gap-1 text-[10px] text-amber-300"><AlertTriangle className="w-3 h-3" /> shot-only override — breaks scene consistency</span>}
        </div>

        {/* Candidates */}
        {loading ? (
          <div className="py-10 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : cards.length === 0 ? (
          <div className="py-8 text-center text-[12px] text-white/50">No {kind} cards in this project's Asset Bible.</div>
        ) : (
          <div className="grid grid-cols-4 gap-2 mb-4" data-testid="swap-candidates">
            {cards.map((c) => (
              <button key={c.id} data-testid={`swap-card-${c.id}`} onClick={() => setPicked(c)}
                className={`rounded-lg border p-1.5 text-left transition-colors ${picked?.id === c.id ? 'border-fuchsia-400/70 bg-fuchsia-500/10' : 'border-white/10 hover:border-white/25 bg-white/5'}`}>
                {c.image ? <img src={c.image} alt={c.name} className="w-full aspect-square object-cover rounded-md mb-1" /> : <div className="w-full aspect-square rounded-md bg-white/5 mb-1" />}
                <div className="text-[10px] text-white/80 truncate">{c.name}</div>
                {c.role && <div className="text-[9px] text-white/40 truncate">{c.role}</div>}
              </button>
            ))}
          </div>
        )}

        {/* Blast radius before commit */}
        <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
          <div className="text-[11px] text-white/60" data-testid="swap-blast-radius">
            {blast ? <>Blast radius: <span className="text-amber-300 font-semibold">stales {blast.stales_sketches} sketch{blast.stales_sketches === 1 ? '' : 'es'}, {blast.stales_keyframes} keyframe{blast.stales_keyframes === 1 ? '' : 's'}</span> across {blast.shots_affected} shot(s)</> : 'Calculating blast radius…'}
          </div>
          <button data-testid="swap-apply" disabled={!picked || busy} onClick={apply}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold bg-emerald-500/25 text-emerald-100 hover:bg-emerald-500/40 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply swap
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
